/**
 * STABILITY-CRITICAL: ÚNICO caminho do SAIR. Sequência: pausa o loop -> gate (push + recontagem)
 * -> backup sempre -> apaga (allowlist menos a sessão) -> reload. Qualquer falha antes do
 * apagamento retoma o loop e devolve success:false sem apagar nada.
 * Antes de alterar, leia docs/llm/slices/sync-engine.md e alinhe com o responsável pelo sync.
 */
import { buildDiscardPayload } from './safeguards'
import type { FlowUi, SafeguardOutcome } from './safeguards'
import type { FlowEventBuffer, RecordFn } from './flowEvents'

export type LogoutDeps = {
  buffer: FlowEventBuffer
  record: RecordFn
  pauseLoop(): void
  resumeLoop(): void
  safeguards(a: {
    mode: 'saida'
    ui: FlowUi
    emit: FlowEventBuffer['emit']
  }): Promise<SafeguardOutcome>
  writeTrail(): Promise<void>
  pushEventsBestEffort(): Promise<void>
  wipe(): Promise<unknown>
}

export type LogoutResult =
  { success: true } | { success: false; reason: string; message: string }

export async function performLogout(opts: {
  ui: FlowUi
  deps?: LogoutDeps
}): Promise<LogoutResult> {
  const d =
    opts.deps ?? (await (await import('./defaultDeps')).createLogoutDeps())
  const { buffer } = d
  buffer.emit({ level: 'info', category: 'auth', action: 'logout_started' })
  d.pauseLoop()
  try {
    const out = await d.safeguards({
      mode: 'saida',
      ui: opts.ui,
      emit: buffer.emit,
    })
    if (!out.ok) {
      d.resumeLoop()
      await buffer.flush(d.record) // nada foi apagado: os eventos viram eventos normais
      return { success: false, reason: out.reason, message: out.message }
    }
    opts.ui.setStep(3, 'Apagando os dados deste aparelho...')
    if (out.mustConfirm) {
      buffer.emit({
        level: 'warn',
        category: 'sync',
        action: 'dlq_discarded_on_reset',
        payload: buildDiscardPayload('saida', out),
      })
    }
    try {
      await d.writeTrail() // 'S - <data>' — a poda mantém as últimas 30
    } catch {
      /* melhor esforço */
    }
    buffer.emit({
      level: 'info',
      category: 'auth',
      action: 'logout_completed',
      payload: { backup: out.savedWith },
    })
    await buffer.flush(d.record)
    try {
      await d.pushEventsBestEffort() // última chance de os eventos saírem antes de apagar
    } catch {
      /* melhor esforço */
    }
    opts.ui.finishing()
    await d.wipe()
    return { success: true }
  } catch (e) {
    d.resumeLoop()
    await buffer.flush(d.record)
    return {
      success: false,
      reason: 'error',
      message: e instanceof Error ? e.message : String(e),
    }
  }
}
